import React, { useMemo } from "react";
import useApplicationStore, {
  RiskValues,
} from "../../../state/ApplicationStore";

import useSavingsProfiles from "../../../hooks/useSavingsProfiles";
import useDataStore from "../../../state/DataStore";
import {
  PensionProductType,
  PensionProductTypeData,
} from "../../../data/PensionProductType";
import computeFees from "../../../util/FeeComputationService";
import ExpandedSavingsProfileRow from "./ExpandedSavingsProfileRow";
import { LoadingSpinner, Table } from "frno-react";
import sort from "../../../util/SortService";
import { SavingsProfile } from "../../../types/SavingsProfile";
import useSavingsProfileTableConfig from "./hooks/savingsProfileTableConfig";
import useTextStore from "../../../state/TextStore";
import "./table-savings-profiles.scss";
import {
  convertToIntegerValue,
  convertToPercentValue,
  getPostfix,
} from "../../../util/data-helper";

type SavingsProfilesTableProps = {
  pensionProductType: PensionProductType;
};
export default function SavingsProfilesTable({
  pensionProductType,
}: SavingsProfilesTableProps) {
  const {
    selectedCompany,
    savingsValue,
    selectedRisk,
    selectedReturnPeriod,
    tableAutoScroll,
    updateAutoScroll,
  } = useApplicationStore();
  const { isLoading } = useSavingsProfiles(
    PensionProductTypeData[pensionProductType].apiId,
  );
  const { headers, sortableColumnsConfig } = useSavingsProfileTableConfig();
  const { savingsProfiles } = useDataStore();
  const { getTextByKey } = useTextStore();

  const UNKNOWN_VALUE = getTextByKey("unknown.value");

  function generateRowData(
    savingsProfiles: SavingsProfile[],
    selectedRisk: RiskValues,
    savingsValue: number,
    selectedCompany: string,
    selectedReturnPeriod: string,
  ) {
    return savingsProfiles
      .filter(
        (profile) =>
          profile.riskClass === null ||
          (profile.riskClass >= selectedRisk.min &&
            profile.riskClass <= selectedRisk.max),
      )
      .map((profile) => {
        return {
          ...profile,
          activePensionProduct: {
            ...profile.activePensionProduct,
            computedFees: computeFees(
              profile.activePensionProduct,
              savingsValue,
            ),
          },
        };
      })
      .map((profile) => {
        const nettoReturn =
          profile.annualReturns[selectedReturnPeriod]?.nettoReturn;
        return {
          rowId: profile.id,
          rowContent: [
            {
              columnKey: "savings_profile",
              cellData: {
                value: profile.name,
              },
            },
            {
              columnKey: "company",
              cellData: {
                value: profile.activePensionProduct.company?.name ?? "Ukjent",
              },
            },
            {
              columnKey: "return",
              mobileHeaderValue: getTextByKey(
                "page.common.table.header.mobile.return",
              ),
              cellData: {
                value: convertToPercentValue(nettoReturn, UNKNOWN_VALUE),
                postfix: getPostfix(nettoReturn),
              },
            },
            {
              columnKey: "risk",
              mobileHeaderValue: getTextByKey(
                "page.common.table.header.mobile.risk",
              ),
              cellData: {
                value: convertToIntegerValue(profile.riskClass, UNKNOWN_VALUE),
              },
            },
            {
              columnKey: "total_fee",
              cellData: [
                {
                  value:
                    profile.activePensionProduct.computedFees.totalFee.amount,
                  postfix: "kr",
                },
                {
                  value:
                    profile.activePensionProduct.computedFees.totalFee.percent,
                  postfix: "%",
                },
              ],
            },
          ],
          expandedRowContent: (
            <ExpandedSavingsProfileRow savingsProfile={profile} />
          ),
          highlightRow:
            profile.activePensionProduct?.company?.name === selectedCompany,
        };
      });
  }

  const tableContent = useMemo(() => {
    return generateRowData(
      savingsProfiles,
      selectedRisk,
      savingsValue,
      selectedCompany,
      selectedReturnPeriod,
    );
  }, [
    savingsProfiles,
    selectedRisk,
    savingsValue,
    selectedCompany,
    selectedReturnPeriod,
  ]);

  return !isLoading ? (
    <Table
      className="table-savings-profiles"
      headerContent={headers}
      bodyContent={tableContent}
      shouldAutoScroll={tableAutoScroll}
      onScrollComplete={() => updateAutoScroll(false)}
      sortConfig={{
        onSort: (rowData, orderBy) => sort(rowData, orderBy),
        sortableColumns: sortableColumnsConfig,
        defaultSortColumn: "total_fee",
        secondarySortColumn: "total_fee",
      }}
    />
  ) : (
    <LoadingSpinner />
  );
}
